import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * Centered modal. Portaled to document.body so it is never clipped by
 * orchestration chat column / messages overflow. Header + sticky footer stay
 * visible; body scrolls.
 */
export function Modal({
  open,
  onClose,
  title,
  eyebrow,
  children,
  footer,
  width,
  headerAction,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  eyebrow?: string;
  children: ReactNode;
  footer?: ReactNode;
  width?: number | string;
  headerAction?: ReactNode;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const handler = () => onClose();
    dialog.addEventListener('close', handler);
    return () => dialog.removeEventListener('close', handler);
  }, [onClose]);

  if (typeof document === 'undefined' || !open) return null;

  return createPortal(
    <dialog
      ref={dialogRef}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        width: width ?? 'min(640px, 92vw)',
        // No `display` here — a native <dialog> is display:none until showModal();
        // forcing display:flex would make every closed modal render. The inner
        // container owns the flex column layout instead.
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        maxHeight: '90vh',
        margin: 0,
        background: 'transparent',
        border: 0,
        padding: 0,
        color: 'inherit',
        zIndex: 10050,
      }}
    >
      <div
        className="fk-investigation-details-dialog"
        style={{
          width: '100%',
          maxHeight: 'min(800px, 88vh)',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <div className="fk-investigation-details-dialog__header" style={{ flexShrink: 0 }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--sp-1)',
              flex: 1,
              minWidth: 0,
            }}
          >
            {eyebrow ? <span className="t-mono-label">{eyebrow}</span> : null}
            <span className="fk-investigation-details-dialog__header-title">{title}</span>
          </div>
          {headerAction ? (
            <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              {headerAction}
            </div>
          ) : null}
          <button
            type="button"
            className="fk-investigation-details-dialog__close-btn"
            aria-label="Close"
            onClick={onClose}
          >
            ✕
          </button>
        </div>
        <div
          className="fk-investigation-details-dialog__body"
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
          }}
        >
          {children}
        </div>
        {footer ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 'var(--sp-2)',
              padding: 'var(--sp-4) var(--sp-5)',
              borderTop: '1px solid var(--border-subtle)',
              flexShrink: 0,
              background: 'var(--surface)',
            }}
          >
            {footer}
          </div>
        ) : null}
      </div>
    </dialog>,
    document.body,
  );
}
